import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useCurrentUser } from 'src/hooks';
import { CustomDropdown, UserAvatar } from '..';
import { langs } from 'src/utils';
import { getLangBasename } from 'src/utils/i18n/langBasename';
import ReceiveSvg from '../../images/icons/receive.svg?react';
import CreateSvg from '../../images/icons/create.svg?react';

const PLUS_FIRE_ICON = 'https://assets.share-a-cart.com/images/next/sac_plus_fire.svg';

export const NavUserOrLoginBlock = () => {
  const { user } = useCurrentUser();
  const [open, setOpen] = useState<boolean>(false);
  const [langExpanded, setLangExpanded] = useState<boolean>(false);
  const burgerRef = useRef<HTMLButtonElement>(null);

  const { t, i18n } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();

  const handleClick = () => {
    setOpen((prev) => !prev);
    if (open) setLangExpanded(false);
  };

  const id = open ? 'navbar-menu-popper' : undefined;

  const handleLogout = async () => {
    await fetch(`/signout`);
    queryClient.clear();
    // Keep the active language after logout (getLangBasename() is '' for English, '/es' otherwise).
    return (window.location.href = getLangBasename() || '/');
  };

  const i18nLang = i18n.resolvedLanguage
    ? i18n.resolvedLanguage.toLowerCase()
    : i18n.language.toLowerCase();

  return (
    <>
      {/* Get Plus+/Login pill (lg+, logged-out only) */}
      {!user && (
        <div className='hidden items-center overflow-hidden rounded-full lg:inline-flex'>
          <NavLink
            to='/plus'
            reloadDocument
            className='inline-flex h-10 items-center gap-2 whitespace-nowrap bg-primary-off px-5 font-primary text-base font-semibold text-offWhite transition-all hover:bg-primary-offHover'
          >
            <img src={PLUS_FIRE_ICON} width='25' height='16' alt='' />
            <span className='font-primary'>
              <Trans
                i18nKey='navbar.get_plus_react'
                components={[<span className='text-skyBlue' />]}
              />
            </span>
          </NavLink>
          <span className='w-px self-stretch bg-white/20' />
          <NavLink
            to='/login'
            className='inline-flex h-10 items-center justify-center whitespace-nowrap bg-primary-off px-5 font-secondary text-base font-bold text-offWhite transition-all hover:bg-primary-offHover'
          >
            {t('navbar.login')}
          </NavLink>
        </div>
      )}

      {/* Hamburger button */}
      <button
        ref={burgerRef}
        aria-label='Open menu'
        aria-expanded={open}
        onClick={handleClick}
        className={`inline-flex cursor-pointer items-center gap-3 rounded-full bg-primary-off transition-all hover:bg-primary-offHover ${
          user
            ? 'h-9 w-[60px] justify-center xs:h-auto xs:w-auto xs:justify-start xs:py-1 xs:pl-1 xs:pr-4 md:h-10'
            : 'h-9 w-[60px] justify-center md:h-10'
        }`}
      >
        {user && (
          <span className='hidden xs:contents'>
            <UserAvatar name={user.name} photo={user.photo} />
          </span>
        )}
        {/* Animated hamburger bars (20×20 container matching EJS) */}
        <div style={{ height: 20, position: 'relative', width: 20, flexShrink: 0 }}>
          <div
            style={{
              transition: `all 0.1385s cubic-bezier(0, 0, 0, 1) ${open ? '0s' : '0.1385s'}`,
              transform: open ? 'translateY(5px)' : 'none',
            }}
          >
            <div
              style={{
                background: 'rgb(176, 176, 209)',
                height: 2,
                left: 2,
                position: 'absolute',
                width: 16,
                top: 4,
                transition: `all 0.1385s cubic-bezier(0, 0, 0, 1) ${open ? '0.1385s' : '0s'}`,
                transform: open ? 'rotate(45deg)' : 'none',
              }}
            />
          </div>
          <div
            style={{ transition: 'all 0.1385s cubic-bezier(0, 0, 0, 1) 0s', opacity: open ? 0 : 1 }}
          >
            <div
              style={{
                background: 'rgb(176, 176, 209)',
                height: 2,
                left: 2,
                position: 'absolute',
                width: 16,
                top: 9,
                transition: 'all 0.1385s cubic-bezier(0, 0, 0, 1) 0s',
              }}
            />
          </div>
          <div
            style={{
              transition: `all 0.1385s cubic-bezier(0, 0, 0, 1) ${open ? '0s' : '0.1385s'}`,
              transform: open ? 'translateY(-5px)' : 'none',
            }}
          >
            <div
              style={{
                background: 'rgb(176, 176, 209)',
                height: 2,
                left: 2,
                position: 'absolute',
                width: 16,
                top: 14,
                transition: `all 0.1385s cubic-bezier(0, 0, 0, 1) ${open ? '0.1385s' : '0s'}`,
                transform: open ? 'rotate(-45deg)' : 'none',
              }}
            />
          </div>
        </div>
      </button>

      <CustomDropdown id={id} open={open} anchorEl={burgerRef.current} handleClick={handleClick}>
        <div className='relative mt-2 flex w-full min-w-[256px] flex-col items-start justify-center rounded-lg border border-gray-300 bg-cloudGray py-5 font-secondary text-base font-semibold'>
          {user ? (
            <>
              {/* Receive/Create (sm:hidden, first at <sm) */}
              <div className='flex w-full flex-col gap-2 px-5 pb-5 sm:hidden'>
                <NavLink
                  to='/get'
                  reloadDocument
                  onClick={handleClick}
                  className='inline-flex h-10 w-full items-center justify-center gap-3 rounded-full bg-primary px-5 font-secondary text-base font-bold text-offWhite'
                >
                  {t('common.receive_cart')}
                  <ReceiveSvg width={20} className='shrink-0 fill-offWhite' />
                </NavLink>
                <NavLink
                  to='/make'
                  reloadDocument
                  onClick={handleClick}
                  className='inline-flex h-10 w-full items-center justify-center gap-3 rounded-full bg-primary px-5 font-secondary text-base font-bold text-offWhite'
                >
                  {t('common.create_cart')}
                  <CreateSvg width={20} className='shrink-0 fill-offWhite' />
                </NavLink>
              </div>

              {/* User items */}
              <div className='flex w-full flex-col gap-1 border-t border-gray-300 px-5 pb-5 pt-5 sm:border-t-0 sm:pt-0'>
                <NavLink
                  className='w-full whitespace-nowrap rounded-md px-1 py-0.5 text-left text-primary transition-all hover:bg-gray-300/60'
                  to='/plus/cart-history'
                  onClick={handleClick}
                >
                  {t('navbar.my_carts')}
                </NavLink>
                <NavLink
                  className='w-full whitespace-nowrap rounded-md px-1 py-0.5 text-left text-primary transition-all hover:bg-gray-300/60'
                  to='/plus/my-account'
                  onClick={handleClick}
                >
                  {t('navbar.my_account')}
                </NavLink>
                {user.subscription.tier === 'organization' &&
                user.subscription.role !== 'admin' ? null : (
                  <NavLink
                    className='w-full cursor-pointer whitespace-nowrap rounded-md px-1 py-0.5 text-left text-primary transition-all hover:bg-gray-300/60'
                    onClick={handleClick}
                    to={
                      user.subscription.tier === 'free'
                        ? '/plus/upgrade'
                        : '/plus/manage-subscription'
                    }
                  >
                    {user.subscription.tier === 'free'
                      ? 'Upgrade'
                      : t('common.manage_subscription')}
                  </NavLink>
                )}
                {user.subscription.tier === 'organization' && (
                  <>
                    <NavLink
                      className='w-full cursor-pointer whitespace-nowrap rounded-md px-1 py-0.5 text-left text-primary transition-all hover:bg-gray-300/60'
                      onClick={handleClick}
                      to='/plus/your-org'
                    >
                      {t('common.organization_settings')}
                    </NavLink>
                    <NavLink
                      className='w-full cursor-pointer whitespace-nowrap rounded-md px-1 py-0.5 text-left text-primary transition-all hover:bg-gray-300/60'
                      onClick={handleClick}
                      to='/plus/setup'
                      reloadDocument
                    >
                      Setup Guide
                    </NavLink>
                  </>
                )}
                <button
                  className='w-full whitespace-nowrap rounded-md px-1 py-0.5 text-left text-primary transition-all hover:bg-gray-300/60'
                  onClick={() => {
                    handleLogout();
                    handleClick();
                  }}
                >
                  {t('navbar.logout')}
                </button>
              </div>

              {/* Free tier: Get Plus+ upsell */}
              {(!user.subscription || user.subscription.tier === 'free') && (
                <div className='flex w-full flex-col gap-2 border-t border-gray-300 px-5 py-5'>
                  <NavLink
                    to='/plus'
                    reloadDocument
                    onClick={handleClick}
                    className='inline-flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-off px-5 font-primary text-base font-semibold text-offWhite'
                  >
                    <img src={PLUS_FIRE_ICON} width='25' height='16' alt='' />
                    <span className='font-primary'>
                      <Trans
                        i18nKey='navbar.get_plus_react'
                        components={[<span className='text-skyBlue' />]}
                      />
                    </span>
                  </NavLink>
                </div>
              )}

              {/* Solutions (lg:hidden) */}
              <div className='flex w-full flex-col gap-2 border-t border-gray-300 px-5 py-5 lg:hidden'>
                <p className='text-sm font-normal text-primary-dark'>{t('navbar.solutions')}</p>
                <NavLink
                  to='/solutions/consumers'
                  reloadDocument
                  onClick={handleClick}
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('solutions.for_consumers')}
                </NavLink>
                <NavLink
                  to='/solutions/schools'
                  reloadDocument
                  onClick={handleClick}
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('solutions.for_schools')}
                </NavLink>
                <NavLink
                  to='/solutions/businesses'
                  reloadDocument
                  onClick={handleClick}
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('solutions.for_businesses')}
                </NavLink>
              </div>

              {/* Helpful links */}
              <div className='flex w-full flex-col gap-2 border-t border-gray-300 px-5 py-5'>
                <a
                  href='/supported'
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('footer.supported_stores')}
                </a>
                <a
                  href='/faq'
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('footer.faqs')}
                </a>
                <a
                  href='/guide'
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('footer.how_to_guides')}
                </a>
              </div>
            </>
          ) : (
            <>
              {/* Receive/Create (sm:hidden, first at <sm) */}
              <div className='flex w-full flex-col gap-2 px-5 pb-5 sm:hidden'>
                <NavLink
                  to='/get'
                  reloadDocument
                  onClick={handleClick}
                  className='inline-flex h-10 w-full items-center justify-center gap-3 rounded-full bg-primary px-5 font-secondary text-base font-bold text-offWhite'
                >
                  {t('common.receive_cart')}
                  <ReceiveSvg width={20} className='shrink-0 fill-offWhite' />
                </NavLink>
                <NavLink
                  to='/make'
                  reloadDocument
                  onClick={handleClick}
                  className='inline-flex h-10 w-full items-center justify-center gap-3 rounded-full bg-primary px-5 font-secondary text-base font-bold text-offWhite'
                >
                  {t('common.create_cart')}
                  <CreateSvg width={20} className='shrink-0 fill-offWhite' />
                </NavLink>
              </div>

              {/* Solutions (md:hidden) */}
              <div className='flex w-full flex-col gap-2 border-t border-gray-300 px-5 pb-5 pt-5 sm:border-t-0 sm:pt-0 md:hidden'>
                <p className='text-sm font-normal text-primary-dark'>{t('navbar.solutions')}</p>
                <NavLink
                  to='/solutions/consumers'
                  reloadDocument
                  onClick={handleClick}
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('solutions.for_consumers')}
                </NavLink>
                <NavLink
                  to='/solutions/schools'
                  reloadDocument
                  onClick={handleClick}
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('solutions.for_schools')}
                </NavLink>
                <NavLink
                  to='/solutions/businesses'
                  reloadDocument
                  onClick={handleClick}
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('solutions.for_businesses')}
                </NavLink>
              </div>

              {/* Get Plus+/Login (lg:hidden, no border-t) */}
              <div className='flex w-full flex-col gap-2 px-5 pb-5 lg:hidden'>
                <NavLink
                  to='/plus'
                  reloadDocument
                  onClick={handleClick}
                  className='inline-flex h-10 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-off px-5 font-primary text-base font-semibold text-offWhite'
                >
                  <img src={PLUS_FIRE_ICON} width='25' height='16' alt='' />
                  <span className='font-primary'>
                    <Trans
                      i18nKey='navbar.get_plus_react'
                      components={[<span className='text-skyBlue' />]}
                    />
                  </span>
                </NavLink>
                <NavLink
                  to='/login'
                  onClick={handleClick}
                  className='inline-flex h-10 w-full items-center justify-center rounded-full bg-primary-off px-5 font-secondary text-base font-bold text-offWhite'
                >
                  {t('navbar.login')}
                </NavLink>
              </div>

              {/* Helpful links (lg:pt-0 lg:border-t-0 when first visible section at lg+) */}
              <div className='flex w-full flex-col gap-2 border-t border-gray-300 px-5 pb-5 pt-5 lg:border-t-0 lg:pt-0'>
                <a
                  href='/supported'
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('footer.supported_stores')}
                </a>
                <a
                  href='/faq'
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('footer.faqs')}
                </a>
                <a
                  href='/guide'
                  className='w-full rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
                >
                  {t('footer.how_to_guides')}
                </a>
              </div>
            </>
          )}

          {/* Language selector (always, with border-t) */}
          <div className='flex w-full flex-col gap-2 border-t border-gray-300 px-5 pt-5'>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLangExpanded((prev) => !prev);
              }}
              className='inline-flex w-full items-center justify-between rounded-md px-1 py-0.5 font-semibold text-primary hover:bg-gray-300/60'
            >
              <span>
                {i18nLang === 'en'
                  ? 'English (US)'
                  : `${langs[i18nLang]?.nameNative} (${langs[i18nLang]?.nameBase})` ||
                    'English (US)'}
              </span>
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className={`transition-transform duration-200 ${langExpanded ? '-rotate-180' : ''}`}
              >
                <path
                  d='M5 7.5L10 12.5L15 7.5'
                  stroke='#3032DC'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
            {langExpanded && (
              <div className='flex flex-col gap-1 pl-4'>
                {Object.keys(langs).map((lang) => {
                  const isCurrentLang =
                    i18nLang === lang || (i18nLang === 'zh_cn' && lang === 'zh');
                  return (
                    <button
                      key={lang}
                      onClick={() => {
                        const prefix = lang === 'en' ? '' : `/${lang}`;
                        // Full reload re-bootstraps i18n + router basename under the new language.
                        window.location.assign(`${prefix}${location.pathname}${location.search}${location.hash}`);
                        handleClick();
                      }}
                      className={`w-full rounded-md px-1 py-0.5 text-left font-semibold ${isCurrentLang ? 'text-primary-dark' : 'text-primary hover:bg-gray-300/60'}`}
                    >
                      {isCurrentLang ? '→ ' : ''}
                      {lang === 'en'
                        ? 'English (US)'
                        : `${langs[lang].nameNative} (${langs[lang].nameBase})`}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CustomDropdown>
    </>
  );
};
